<template>
  <v-sheet class="funding-settings mt-6 pa-4" outlined>
    <div class="d-flex align-start justify-space-between flex-wrap">
      <div class="pr-4">
        <div class="font-weight-bold text-subtitle-1">목표형 프로모션</div>
        <div class="text-caption grey--text text--darken-1">
          기존 프로모션의 실제 결제액을 컨셉 공개 화면에 표시합니다.
        </div>
      </div>
      <v-switch
        class="mt-0"
        color="primary"
        hide-details
        inset
        :input-value="draft.funding_enabled"
        label="펀딩 목록에 공개"
        @change="setValue('funding_enabled', Boolean($event))"
      ></v-switch>
    </div>

    <v-expand-transition>
      <div v-if="draft.funding_enabled">
        <v-divider class="my-5"></v-divider>

        <div class="funding-group-title">기존 프로모션 연결</div>
        <v-alert v-if="promotionLoadFailed" dense text type="warning">
          프로모션 목록을 불러오지 못했습니다. 잠시 뒤 다시 열어 주세요.
        </v-alert>
        <v-select
          class="rounded-10"
          dense
          outlined
          clearable
          label="결제액을 집계할 프로모션 코드"
          hint="이 코드로 결제된 금액에서 취소·환불액을 제외해 표시합니다."
          persistent-hint
          :items="promotionItems"
          :loading="promotionsLoading"
          :value="draft.promotion_code"
          @input="setValue('promotion_code', $event || '')"
        ></v-select>

        <div class="funding-group-title mt-5">공개 주소와 순서</div>
        <v-row dense>
          <v-col cols="12" md="7">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              label="공개 주소"
              prefix="/funding/"
              hint="영문 소문자, 숫자, 하이픈만 사용할 수 있습니다."
              persistent-hint
              :value="draft.slug"
              @input="setValue('slug', normalizeSlug($event))"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="5">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              type="number"
              min="0"
              label="목록 노출 순서"
              :value="draft.display_order"
              @input="setValue('display_order', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <v-select
          class="rounded-10"
          dense
          outlined
          label="공개 상태"
          :items="visibilityItems"
          :value="draft.visibility"
          @input="setValue('visibility', $event)"
        ></v-select>

        <div class="funding-group-title mt-4">목표와 기간</div>
        <v-text-field
          class="rounded-10"
          dense
          outlined
          type="number"
          min="1"
          suffix="원"
          label="목표금액"
          :value="draft.goal_amount_krw"
          @input="setValue('goal_amount_krw', $event)"
        ></v-text-field>
        <v-row dense>
          <v-col cols="12" md="6">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              type="datetime-local"
              label="시작일시"
              :value="draft.starts_at"
              @input="setValue('starts_at', $event)"
            ></v-text-field>
          </v-col>
          <v-col cols="12" md="6">
            <v-text-field
              class="rounded-10"
              dense
              outlined
              type="datetime-local"
              label="종료일시"
              :value="draft.ends_at"
              @input="setValue('ends_at', $event)"
            ></v-text-field>
          </v-col>
        </v-row>

        <div class="funding-group-title mt-4">사용할 컨셉 사진</div>
        <div v-if="availableImages.length" class="funding-image-grid mb-4">
          <button
            v-for="image in availableImages"
            :key="image.key"
            class="funding-image-choice"
            :class="{ selected: draft.card_image_slot === image.key }"
            type="button"
            @click="setValue('card_image_slot', image.key)"
          >
            <v-img :src="image.src" aspect-ratio="1.6" class="funding-image-preview"></v-img>
            <span>{{ image.label }}</span>
            <small>{{ draft.card_image_slot === image.key ? "목록 사진" : "선택" }}</small>
          </button>
        </div>
        <v-alert v-else dense text type="info" class="mb-4">
          위에서 컨셉 사진을 등록하면 여기에서 목록 사진을 고를 수 있습니다.
        </v-alert>
        <v-select
          class="rounded-10"
          dense
          outlined
          label="상세 첫 화면 사진"
          :items="imageItems"
          :disabled="!availableImages.length"
          :value="draft.hero_image_slot"
          @input="setValue('hero_image_slot', $event)"
        ></v-select>

        <div class="funding-group-title mt-4">고객 화면 표시</div>
        <v-row dense>
          <v-col v-for="toggle in displayToggles" :key="toggle.key" cols="12" sm="6">
            <v-switch
              class="mt-0"
              dense
              hide-details
              color="primary"
              :label="toggle.label"
              :input-value="Boolean(draft[toggle.key])"
              @change="setValue(toggle.key, Boolean($event))"
            ></v-switch>
          </v-col>
        </v-row>

        <v-alert class="mt-5 mb-0" dense text type="success">
          기존 예약·결제를 그대로 사용합니다. 실제 결제액에서 취소·환불액을 제외한 금액만 달성액에 반영됩니다.
        </v-alert>
      </div>
    </v-expand-transition>
  </v-sheet>
</template>

<script>
export default {
  props: {
    value: {
      type: Object,
      required: true,
    },
    conceptId: {
      type: [Number, String],
      default: null,
    },
    conceptTitle: {
      type: String,
      default: "",
    },
    images: {
      type: Array,
      default: () => [],
    },
  },

  data: () => ({
    promotions: [],
    promotionsLoading: false,
    promotionLoadFailed: false,
    visibilityItems: [
      { text: "비공개", value: "private" },
      { text: "미리보기", value: "preview" },
      { text: "공개", value: "public" },
    ],
    displayToggles: [
      { key: "show_funded_amount", label: "실제 결제액 공개" },
      { key: "show_progress_rate", label: "달성률 공개" },
      { key: "show_backer_count", label: "참여 인원 공개" },
      { key: "show_countdown", label: "남은 시간 공개" },
    ],
  }),

  computed: {
    draft() {
      return this.value
    },
    availableImages() {
      return this.images.filter(image => image && image.key && image.src)
    },
    imageItems() {
      return this.availableImages.map(image => ({
        text: image.label,
        value: image.key,
      }))
    },
    promotionItems() {
      const conceptId = Number(this.conceptId)
      return [...this.promotions]
        .sort((a, b) => {
          const aSame = Number(a.conceptId) === conceptId ? 0 : 1
          const bSame = Number(b.conceptId) === conceptId ? 0 : 1
          return aSame - bSame
        })
        .map(promotion => ({
          text: `${promotion.name} · ${promotion.code}${
            Number(promotion.conceptId) === conceptId ? " · 현재 컨셉" : ""
          }`,
          value: promotion.code,
        }))
    },
  },

  mounted() {
    this.loadPromotions()
  },

  methods: {
    setValue(key, value) {
      this.$emit("input", { ...this.draft, [key]: value })
    },
    normalizeSlug(value) {
      return String(value || "")
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, "")
        .replace(/-{2,}/g, "-")
        .replace(/^-/, "")
    },
    async loadPromotions() {
      this.promotionsLoading = true
      this.promotionLoadFailed = false
      try {
        const response = await this.$http.post("/api/promotion/select/getAllProductsInfo", {})
        const rows = Array.isArray(response.data) ? response.data : []
        this.promotions = rows
          .filter(item => Number(item.enabled) === 1 && item.code)
          .map(item => ({
            code: String(item.code),
            name: String(item.name || item.code),
            conceptId: Number(item.conceptId) || null,
          }))
      } catch (error) {
        this.promotionLoadFailed = true
      } finally {
        this.promotionsLoading = false
      }
    },
    validate() {
      if (!this.draft.funding_enabled) return []
      const errors = []
      const goal = Number(this.draft.goal_amount_krw)
      const start = new Date(this.draft.starts_at)
      const end = new Date(this.draft.ends_at)

      if (!this.draft.promotion_code) {
        errors.push("결제액을 집계할 프로모션 코드를 선택해 주세요.")
      }
      if (!/^[a-z0-9]+(?:-[a-z0-9]+)*$/.test(this.draft.slug)) {
        errors.push("공개 주소를 확인해 주세요.")
      }
      if (!Number.isInteger(goal) || goal < 1) {
        errors.push("목표금액을 확인해 주세요.")
      }
      if (
        Number.isNaN(start.getTime()) ||
        Number.isNaN(end.getTime()) ||
        start.getTime() >= end.getTime()
      ) {
        errors.push("종료일시는 시작일시보다 뒤여야 합니다.")
      }
      if (!this.draft.card_image_slot || !this.draft.hero_image_slot) {
        errors.push("목록과 상세에 사용할 컨셉 사진을 선택해 주세요.")
      }
      return errors
    },
  },
}
</script>

<style scoped>
.funding-settings {
  border-color: #d8c3ca !important;
  border-radius: 10px;
  background: #fffafb;
}

.funding-group-title {
  margin-bottom: 12px;
  color: #6c3d4b;
  font-size: 14px;
  font-weight: 700;
}

.funding-image-grid {
  display: grid;
  grid-template-columns: repeat(3, minmax(0, 1fr));
  gap: 10px;
}

.funding-image-choice {
  display: grid;
  gap: 5px;
  min-width: 0;
  padding: 7px;
  border: 1px solid #dedede;
  border-radius: 8px;
  background: white;
  color: #4a4a4a;
  text-align: left;
}

.funding-image-choice.selected {
  border-color: #9f5068;
  box-shadow: 0 0 0 2px rgba(159, 80, 104, 0.14);
}

.funding-image-choice span {
  overflow: hidden;
  font-size: 12px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.funding-image-choice small {
  color: #9f5068;
  font-size: 10px;
}

.funding-image-preview {
  border-radius: 5px;
  background: #f2eef0;
}

@media (max-width: 600px) {
  .funding-image-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
